<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <b-row>
      <b-col sm="6">
        <admin-breadcrumb name="Category Images"/>
      </b-col>
      <b-col sm="6" class="text-right align-self-end">
        <b-btn variant="success" @click="$refs.newModal.open()" class="mb-4">Add new Image</b-btn>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Category Images" sub-title="Manage all category images in GreenLists">
          <template v-slot:header-right>
            <b-form-select
                class="w-auto mr-2 mt-2"
                v-model="filters.category_type"
                :options="categoryTypes"
            />
            <b-form-select
                class="w-auto mt-2"
                v-model="filters.category"
                :options="categories"
                :disabled="!filters.category_type"
            />
          </template>
          <div class="admin-category-images mt-3">
            <div
                v-for="(image, index) in categoryImages"
                :key="index"
                class="admin-category-image"
            >
              <b-img-lazy
                  :src="image.url"
                  :alt="image.name"
                  class="admin-category-image-img"
                  thumbnail
                  fluid-grow
              />
              <b-button
                  class="admin-category-image-delete"
                  title="delete image"
                  variant="danger"
                  @click.prevent="deleteCategoryImage(image)"
              >
                Delete
              </b-button>
            </div>
          </div>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-category-image-add-modal ref="newModal" @success="onCategoryImagesAdded"/>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import AdminCategoryImageAddModal from "../components/AdminCategoryImageAddModal";

export default {
  name: "AdminCategoryImages",
  components: {AdminCategoryImageAddModal, AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    return {
      loading: true,
      category_images: [],
      filters: {
        category_type: "",
        category: ""
      }
    }
  },
  methods: {
    onCategoryImagesAdded(categoryImages) {
      let word = categoryImages.length > 1 ? 'images have' : 'image has';
      this.$bvToast.toast(`Category ${word} been successfully added`, {
        title: 'Success!',
        variant: 'success'
      });
      this.category_images = [...this.category_images, ...categoryImages];
    },
    deleteCategoryImage(categoryImage) {
      let ct = this.categoryTypes.find(ct => ct.value === categoryImage.category.category_type_id),
        catLabel = `${ct?.text} - ${categoryImage.category.name}`;
      this.$bvModal.msgBoxConfirm(`You are going to delete a category image for ${catLabel}`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.delete(`${API_ROUTES.ADMIN_TAXONOMY_CATEGORY_IMAGES}/${categoryImage.id}`).then(({data}) => {
            this.$bvToast.toast('Category image has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            let index = this.category_images.findIndex(ci => ci.id === categoryImage.id);
            if (index >= 0) {
              let categoryImages = [...this.category_images];
              categoryImages.splice(index, 1);
              this.category_images = categoryImages;
            }
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    }
  },
  computed: {
    categoryTypes() {
      let categoryTypes = this.$store.state.data.category_types
          .map(categoryType => ({
            value: categoryType.id,
            text: categoryType.name
          }));
      categoryTypes.unshift({
        value: '',
        text: 'All Category Types'
      });
      return categoryTypes;
    },
    categories() {
      let categories = this.$store.state.data.categories
          .map(category => ({
            value: category.id,
            text: category.name,
            category_type_id: category.category_type_id
          }));
      if (this.filters.category_type) {
        categories = categories.filter(category => category.category_type_id === this.filters.category_type);
      }
      categories.unshift({
        value: '',
        text: 'All Categories'
      });
      return categories;
    },
    categoryImages() {
      let categoryImages = this.category_images;
      if (this.filters.category_type) {
        categoryImages = categoryImages.filter(ci => ci.category.category_type_id === this.filters.category_type)
      }
      if (this.filters.category) {
        categoryImages = categoryImages.filter(ci => ci.category.id === this.filters.category)
      }

      return categoryImages;
    }
  },
  watch: {
    "filters.category_type": function (categoryTypeId) {
      if (this.filters.category) {
        let category = this.$store.state.data.categories.find(
            category => category.id === this.filters.category && category.category_type_id === categoryTypeId
        );
        if (!category) {
          this.filters.category = '';
        }
      }
      this.$refs.newModal.setCategoryType(categoryTypeId);
    },
    "filters.category": function (categoryId) {
      this.$refs.newModal.setCategory(categoryId);
    }
  },
  created() {
    this.$api.get(API_ROUTES.ADMIN_TAXONOMY_CATEGORY_IMAGES).then(({data}) => {
      this.category_images = data.category_images;
    }).catch(({response}) => {
      this.$bvToast.toast('Failed to load category images', {
        title: 'Error!',
        variant: 'danger'
      });
    }).finally(() => {
      this.loading = false;
    });
  }
}
</script>

<style lang="scss" scoped>
.admin-category-images{
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  gap: 15px;
  @media screen and (max-width: 1399px){
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media screen and (max-width: 1269px) and (min-width: 1170px){
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media screen and (max-width: 969px){
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media screen and (max-width: 769px){
    grid-template-columns: 1fr 1fr;
  }
  @media screen and (max-width: 499px){
    grid-template-columns: 1fr;
  }
  .admin-category-image{
    aspect-ratio : 1 / 1;
    position: relative;
    &-img{
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
    &-delete{
      position: absolute;
      top: 15px;
      right: 15px;
      opacity: 0;
      transition: all .3s ease-in-out;
    }
    &:hover{
      .admin-category-image-delete{
        opacity: 1;
      }
    }
  }
}
</style>
